import { Box, SystemStyleObject } from '@chakra-ui/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Cell, Row } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import {
  FloatingButtonsContainer,
  TableRowFloatingButtonsContext,
} from '../floating-action-buttons';
import { useActiveRowContext } from './active-row-context';
import { TableItem, type TableRowClickHandler } from './table';
import { TableActionHandler } from './table-action';
import { TableCell } from './table-cell';

export interface TableRowProps<T> {
  itemName: TableItem;
  row: Row<T>;
  rowStyles: SystemStyleObject;
  isReorderable?: boolean;
  actionHandler: TableActionHandler<T>;
  onRowClick?: TableRowClickHandler<T>;
}

export function TableRow<T>({
  itemName,
  row,
  rowStyles,
  isReorderable,
  actionHandler,
  onRowClick,
}: TableRowProps<T>) {
  const { transform, transition, isDragging, setNodeRef } = useSortable({ id: row.id });
  const activeRow = useActiveRowContext();
  const isActive = activeRow === row.id;
  const [isRowHovered, setIsRowHovered] = useState(false);
  const isRowClickSupported = !!onRowClick && (onRowClick.isSupported?.(row) ?? true);

  const cellStyles = useMemo(
    () => ({
      position: 'relative',
      display: 'flex',
      p: 0,
      fontSize: 'sm',
      lineHeight: '4',
      backgroundClip: 'padding-box',
      ':nth-of-type(1)': {
        paddingLeft: isReorderable ? 4 : 1,
      },
      '&.why-table--cell': {
        '&--editable': {
          cursor: 'pointer',
          '&:after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            boxShadow: 'transparent 0px 0px 2px 1px inset',
            pointerEvents: 'none',
          },
          '&:hover:after': {
            boxShadow: `var(--chakra-colors-blue-200) 0px 0px 2px 1px inset`,
          },
        },
      },
    }),
    [isReorderable],
  );

  const cellBoxProps = useMemo(() => ({ __css: cellStyles }), [cellStyles]);
  const cells = row.getVisibleCells();

  function renderCell(cell: Cell<T, unknown>) {
    return (
      <TableCell
        key={cell.id}
        cell={cell}
        boxProps={cellBoxProps}
        isReorderable={isReorderable}
        actionHandler={actionHandler}
      />
    );
  }

  return (
    <TableRowFloatingButtonsContext.Provider value={true}>
      <FloatingButtonsContainer
        as={Box}
        showButtons={isRowHovered}
        onMouseEnter={() => setIsRowHovered(true)}
        onMouseLeave={() => setIsRowHovered(false)}
        data-testid={`table-${itemName.singular}-row-${row.id}`}
        cursor={isRowClickSupported ? 'pointer' : undefined}
        onClick={isRowClickSupported ? () => onRowClick?.(row) : undefined}
        role="row"
        className={`why-table--row ${isActive ? 'why-table--row-active' : ''}`}
        __css={rowStyles}
        transform={CSS.Translate.toString(transform)}
        transition={transition}
        zIndex={isDragging ? 1 : undefined}
        ref={setNodeRef}
      >
        {cells.map(renderCell)}
      </FloatingButtonsContainer>
    </TableRowFloatingButtonsContext.Provider>
  );
}
